import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createTenantPeopleSource } from "@playbookai/api-clients";
import type { PeoplePerson, PeopleSource } from "@playbookai/types";
import {
  Button,
  DescriptionItem,
  DescriptionList,
  FormField,
  Modal,
  PeopleField,
  type PeopleFieldLabels,
  type PeopleFieldValue,
} from "@playbookai/ui";
import { resolveTenantPeople } from "../../api/tenant-people-source.js";
import {
  AdminApiError,
  adminErrorDetail,
  adminLamaOwnershipQueryKey,
  adminLeadRemovalQueryKey,
  adminMembersQueryKey,
  getLamaOwnership,
  getTransformationLeadRemoval,
  listAdminMembers,
  TRANSFORMATION_LEAD_ROLE,
  type AdminMember,
  type AdminMemberFilters,
  type LamaOwnershipInventory,
  type TransformationLeadRemovalOperation,
  type TransformationLeadRemovalStage,
} from "../../api/admin-users-api.js";

/**
 * Removing Transformation Lead is the one role change that MOVES things
 * (ADR-0078): every LAMA questionnaire and campaign the member owns has to go
 * to another active lead before the role can be revoked, and the server refuses
 * to guess who. This dialog exists to ask — and to make the multi-stage,
 * cross-service operation behind the button RESUMABLE from the client side.
 *
 * Four things about it are deliberate:
 *
 * - **It reads COUNTS, never content.** The ownership inventory says how many
 *   questionnaires and campaigns the member owns, so the administrator learns
 *   whether a replacement is needed BEFORE committing — but never a title or an
 *   id. A Tenant Admin who is not a Transformation Lead must not be able to
 *   inspect LAMA management data from here (plan §7.3, AC7).
 * - **The idempotency key is the OPERATION's, not the dialog's.** A removal
 *   that stopped midway is resumable only under the key it was recorded with,
 *   and the server has no worker that finishes it alone. So on open the dialog
 *   asks the server for the member's in-flight operation and ADOPTS its key,
 *   stage and replacement; only when there is none does it mint a fresh key.
 *   That is what makes Try again work after a close, a navigation, a reload,
 *   or from another administrator's screen — and why a `removal_in_progress`
 *   refusal (a race with an operation recorded a moment ago) is handled by
 *   adopting the key the server names, never by asking anyone to wait.
 * - **Fails closed while it does not know.** Confirm is disabled until both
 *   the recovery read and the inventory have answered; an error on either
 *   keeps it disabled with the reason visible, because guessing "nothing in
 *   flight" would mint a second key and be refused, and guessing "owns
 *   nothing" would send a removal the server will refuse anyway.
 * - **Replacement recovery is stage-aware, like the server.** Before anything
 *   was transferred a different replacement is simply chosen; after the
 *   transfer the recorded person received it, and a new pick is accepted only
 *   when they are no longer eligible — receiving what REMAINS. The copy says
 *   which case the administrator is in.
 *
 * The replacement picker offers ACTIVE members who currently hold the role,
 * minus the member being removed, straight from the members endpoint's `role`
 * filter — the same live fact the server re-verifies on the write. It is the
 * shared PeopleField, so it searches from two letters; page one (fifty leads)
 * still answers whether anyone else holds the role, and names the chip of a
 * recorded replacement without asking the server who they are.
 */

export interface TransformationLeadRemovalTarget {
  readonly userId: string;
  /** Display name or email — whatever the row showed. */
  readonly label: string;
}

export interface TransformationLeadRemovalInput {
  readonly replacementUserId: string | null;
  readonly idempotencyKey: string;
}

export interface TransformationLeadRemovalDialogProps {
  readonly tenantId: string | null;
  /** The signed-in administrator, so a resumed operation can say whose it was. */
  readonly currentUserId?: string | null;
  /** `null` closes the dialog. */
  readonly member: TransformationLeadRemovalTarget | null;
  readonly onClose: () => void;
  /**
   * Runs the removal. Resolves once the operation is COMPLETE; rejects with the
   * `AdminApiError` the server answered, which this dialog translates. The
   * caller owns the request so the two entry points — the Members row and a
   * group-rule revocation — reach their own endpoints with one dialog.
   */
  readonly onConfirm: (input: TransformationLeadRemovalInput) => Promise<void>;
  /**
   * Named so a later reader of the transcript knows what the member has been
   * removed from: the direct row says "the role", the rule page says "the
   * rule's implied role".
   */
  readonly description?: string;
}

/* The server's refusal codes, in the administrator's words. Anything unmapped
   falls back to the server's own sentence, which PB-540 made safe to render. */
const REMOVAL_ERROR_COPY: Record<string, string> = {
  replacement_required:
    "{member} still owns LAMA questionnaires or campaigns. Choose a replacement Transformation Lead to take them over, then try again.",
  replacement_ineligible:
    "That person can no longer take over: they are not an active Transformation Lead, or a removal of their own is in progress. Choose someone else.",
  removal_in_progress:
    "A removal for {member} was already recorded. This attempt now resumes it — try again.",
  removal_state_changed:
    "The removal advanced while this attempt ran. Try again to continue from where it is now.",
  removal_attempt_in_progress:
    "An attempt on this removal is already running — possibly on another admin's screen. Try again once it has finished.",
  replacement_change_unsafe:
    "The last attempt did not report whether it transferred anything, so the Transformation Lead already recorded here may hold {member}'s LAMA ownership. Finish with them first; if they turn out to be ineligible, you can then choose someone else.",
  role_not_held: "{member} no longer holds Transformation Lead.",
  idempotency_key_conflict:
    "This removal already transferred ownership to a different Transformation Lead. Keep that replacement to finish it.",
  session_invalidation_unavailable:
    "The role is revoked, but {member} could not be signed out everywhere yet. Nothing has been lost: Try again finishes the sign-out.",
  lama_ownership_unavailable:
    "LAMA could not be reached. Nothing has been lost: Try again resumes from where it stopped.",
  /* NOT a twin of `lama_ownership_unavailable` above, and the difference is the
     whole point of carrying it separately: that one is LAMA being briefly
     unreachable, which a retry fixes, while this is the deployment never having
     been given the ownership client at all, which no retry can fix. Both used
     to fall through to the same "Try again resumes from where it stopped"
     fallback, so a permanent configuration gap read as a transient outage —
     which is how PB-5236 came to be diagnosed as an admin lacking LAMA access.
     Nothing has been lost is still true, so it is still said. */
  lama_ownership_client_not_configured:
    "Transformation Lead removal is not configured in this deployment, so {member}'s LAMA ownership cannot be transferred. Nothing has been lost, but retrying will not help — this needs a deployment configuration change.",
};

/**
 * The deployment has no LAMA ownership client, so neither the ownership check
 * nor the removal itself can run here (`assertConfigured` in auth's
 * `transformation-lead-removal.service.ts`).
 *
 * Worth naming rather than folding into the generic failure: it is the ONE
 * ownership-check failure a retry cannot clear, so the branch that reports it
 * must not offer one. It is also not a permission problem — the check reads
 * through to Playbook BE on a SERVICE credential and never on the caller's LAMA
 * access — and reporting it as a bare "could not check" is what made it look
 * like one (PB-5236).
 */
const OWNERSHIP_UNCONFIGURED = "lama_ownership_client_not_configured";

function isOwnershipUnconfigured(error: unknown): boolean {
  return (
    error instanceof AdminApiError && error.code === OWNERSHIP_UNCONFIGURED
  );
}

/* What each recorded stage means, for a resumed operation. */
const STAGE_COPY: Record<TransformationLeadRemovalStage, string> = {
  pending: "It was recorded, and nothing has been transferred yet.",
  cleanup_completed:
    "Their LAMA ownership was transferred and their other LAMA access revoked; the role still has to be revoked and their sessions ended.",
  role_revoked:
    "The role is revoked and they are signed out; a final ownership check is still pending.",
};

const LAST_ERROR_COPY: Record<string, string> = {
  cleanup_unavailable: "LAMA could not be reached on the last attempt.",
  replacement_required:
    "A replacement Transformation Lead is needed to finish.",
  replacement_ineligible:
    "The chosen replacement is no longer eligible; choose someone else.",
  session_invalidation_failed:
    "They could not be signed out everywhere on the last attempt.",
};

function describeRemovalError(error: unknown, memberLabel: string): string {
  if (!(error instanceof AdminApiError)) {
    return "The removal could not be completed. Nothing has been lost — try again.";
  }
  const copy =
    error.code !== undefined && Object.hasOwn(REMOVAL_ERROR_COPY, error.code)
      ? REMOVAL_ERROR_COPY[error.code]
      : undefined;
  if (copy) return copy.replace("{member}", memberLabel);
  /* A 503 with no mapped code (the proxy itself failed) — the reached stage
     HOLDS and the same key resumes it, which is the one thing to hear. */
  return `${error.message || "The removal could not be completed."} Nothing has been lost: Try again resumes from where it stopped.`;
}

function mintIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `tl-removal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function ownedCount(inventory: LamaOwnershipInventory): number {
  return inventory.questionnairesOwned + inventory.campaignsOwned;
}

function countPhrase(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

const LEAD_FILTERS: AdminMemberFilters = {
  role: TRANSFORMATION_LEAD_ROLE,
  status: "active",
};

function leadPerson(lead: AdminMember): PeoplePerson {
  return {
    id: lead.userId,
    name: lead.displayName ?? lead.email ?? lead.userId,
    email: lead.email,
  };
}

/* The field's default words say it searches everyone in the workspace; this
   one searches active Transformation Leads only. The roster only names the
   chip — grant mode `none` lists no roster rows — so "People with access
   still show" after a failed search would be false here. */
const LEAD_FIELD_LABELS: PeopleFieldLabels = {
  searchPlaceholder: "Search Transformation Leads",
  workspace: "Transformation Leads",
  hint: (remaining, typed) =>
    typed
      ? `Type ${remaining} more ${remaining === 1 ? "letter" : "letters"} to search active Transformation Leads.`
      : `Type ${remaining} letters to search active Transformation Leads.`,
  searching: "Searching Transformation Leads",
  searchFailed: "Couldn’t search Transformation Leads right now.",
  searchFailedRoster: "",
};

/* What a recorded replacement's chip reads until the roster or `resolve`
   names them: every id this dialog did not pick itself is the server's. */
const RECORDED_REPLACEMENT = "Recorded replacement";

/** The operation this dialog is driving: adopted from the server, or minted. */
interface Attempt {
  readonly userId: string;
  readonly idempotencyKey: string;
  /** Present when adopted from an in-flight operation (server or refusal). */
  readonly resumed: {
    readonly stage: TransformationLeadRemovalStage;
    readonly lastErrorCode: string | null;
    readonly requestedByUserId: string | null;
    readonly recordedReplacementUserId: string | null;
    /** Another attempt holds the claim: wait rather than start beside it. */
    readonly attemptInProgress: boolean;
    /**
     * Whether the server will accept a DIFFERENT replacement. Taken from the
     * server, never derived here: the rule is stage- and outcome-dependent, and
     * a second copy of it would offer a change the write refuses.
     */
    readonly replacementChangeAllowed: boolean;
  } | null;
  readonly replacementUserId: string;
  readonly error: string | null;
  /**
   * The refusal's machine code, kept beside its rendered copy because one of
   * them is not retryable and the sentence alone cannot say so. Every other
   * refusal here resumes on the next Try again, so the button relabels and
   * stays live off `error`; `lama_ownership_client_not_configured` never can,
   * and labelling it "Try again" contradicts the copy that just said retrying
   * will not help.
   */
  readonly errorCode: string | null;
  readonly pending: boolean;
  /* Set from a `replacement_required` refusal, which is the SERVER saying the
     member owns something even if the inventory read said otherwise — a
     resource created between the read and the click. */
  readonly serverRequiresReplacement: boolean;
}

function adopt(
  userId: string,
  operation: TransformationLeadRemovalOperation,
): Attempt {
  return {
    userId,
    idempotencyKey: operation.idempotencyKey,
    resumed: {
      stage: operation.status,
      lastErrorCode: operation.lastErrorCode,
      requestedByUserId: operation.requestedByUserId,
      recordedReplacementUserId: operation.replacementUserId,
      attemptInProgress: operation.attemptInProgress,
      replacementChangeAllowed: operation.replacementChangeAllowed,
    },
    replacementUserId: operation.replacementUserId ?? "",
    error: null,
    errorCode: null,
    pending: false,
    serverRequiresReplacement:
      operation.lastErrorCode === "replacement_required",
  };
}

function isStage(
  value: string | null,
): value is TransformationLeadRemovalStage {
  return (
    value === "pending" ||
    value === "cleanup_completed" ||
    value === "role_revoked"
  );
}

export function TransformationLeadRemovalDialog({
  tenantId,
  currentUserId = null,
  member,
  onClose,
  onConfirm,
  description,
}: TransformationLeadRemovalDialogProps): ReactElement {
  const queryClient = useQueryClient();
  const open = member !== null;

  /* THE RECOVERY READ. Fresh on every open (`staleTime: 0`), because the whole
     point is to learn about an operation another attempt may just have
     recorded — this dialog's own earlier attempt, or another administrator's. */
  const recovery = useQuery({
    queryKey: adminLeadRemovalQueryKey(tenantId, member?.userId ?? "none"),
    queryFn: ({ signal }) =>
      getTransformationLeadRemoval(member?.userId ?? "", signal),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });

  /* Everything below is keyed on the OPEN — the member plus the key decided for
     them — so a reopen for the same person asks the server again and a switch
     to a different person cannot carry the previous pick across. The key is
     decided only once the recovery read has answered: adopted when there is an
     operation, minted when there is none. */
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const active =
    member && attempt && attempt.userId === member.userId ? attempt : null;

  useEffect(() => {
    if (!member) {
      setAttempt(null);
      return;
    }
    if (recovery.data === undefined) return;
    const operation = recovery.data;
    setAttempt((current) => {
      if (current && current.userId === member.userId) {
        /* The key is decided once, but the SERVER-DERIVED half of `resumed` is
           re-read: a refetch is how a live attempt's claim is seen to lapse,
           and how `replacementChangeAllowed` is learned to have opened up. The
           local pick and error are the operator's and are kept. */
        return operation && operation.idempotencyKey === current.idempotencyKey
          ? { ...current, resumed: adopt(member.userId, operation).resumed }
          : current;
      }
      return operation
        ? adopt(member.userId, operation)
        : {
            userId: member.userId,
            idempotencyKey: mintIdempotencyKey(),
            resumed: null,
            replacementUserId: "",
            error: null,
            errorCode: null,
            pending: false,
            serverRequiresReplacement: false,
          };
    });
  }, [member, recovery.data]);

  const inventory = useQuery({
    queryKey: adminLamaOwnershipQueryKey(tenantId, member?.userId ?? "none"),
    queryFn: ({ signal }) => getLamaOwnership(member?.userId ?? "", signal),
    enabled: open,
    staleTime: 0,
  });

  const leads = useQuery({
    queryKey: adminMembersQueryKey(tenantId, LEAD_FILTERS),
    queryFn: ({ signal }) => listAdminMembers(null, LEAD_FILTERS, signal),
    enabled: open,
    staleTime: 0,
  });
  const removedUserId = member?.userId ?? null;
  const candidates = useMemo(
    () =>
      (leads.data?.users ?? [])
        .filter((candidate) => candidate.userId !== removedUserId)
        .map(leadPerson),
    [leads.data, removedUserId],
  );
  /* One object per tenant, per member and per first page: the field caches
     its searches per source, so a source rebuilt every render would search
     every render, and one kept across a session swap would serve one tenant's
     answers to the next (PB-1556). */
  const replacementSource = useMemo<PeopleSource>(
    () => ({
      ...createTenantPeopleSource({
        search: async (query, cursor, signal) => {
          const page = await listAdminMembers(
            cursor,
            { ...LEAD_FILTERS, query },
            signal,
          );
          return {
            items: page.users
              .filter((candidate) => candidate.userId !== removedUserId)
              .map(leadPerson),
            nextCursor: page.nextCursor,
          };
        },
        resolve: resolveTenantPeople,
      }),
      roster: () => candidates,
    }),
    [tenantId, candidates, removedUserId],
  );
  /* The name of the last person picked from a search, for a chip off page
     one; a name never changes with the dialog's member, so it needs no key. */
  const [picked, setPicked] = useState<{
    readonly id: string;
    readonly name: string;
  } | null>(null);

  const resumed = active?.resumed ?? null;
  /* Past `pending`, the recorded replacement RECEIVED the transfer; the
     replacement can only change if they are no longer eligible, and then only
     for what remains. The copy below says so, and the pick is pre-set. */
  const transferDone = resumed !== null && resumed.stage !== "pending";
  const requiresReplacement =
    (inventory.data ? ownedCount(inventory.data) > 0 : false) ||
    (active?.serverRequiresReplacement ?? false) ||
    (resumed !== null && resumed.recordedReplacementUserId !== null);
  const replacementUserId = active?.replacementUserId ?? "";
  /* Page one names its own leads (the roster) and `resolve` anyone else; the
     name here is only what the chip shows before either has answered. */
  const replacement: PeopleFieldValue =
    replacementUserId === ""
      ? null
      : {
          kind: "person",
          id: replacementUserId,
          name:
            picked?.id === replacementUserId
              ? picked.name
              : RECORDED_REPLACEMENT,
        };
  const inventoryReady = inventory.data !== undefined && !inventory.isError;
  const recoveryReady = recovery.data !== undefined && !recovery.isError;
  /* Another administrator's attempt holds the operation's claim. Starting a
     second one is refused server-side, so the button waits rather than earning
     a 409. */
  const attemptInProgress = resumed?.attemptInProgress ?? false;
  /* The server's answer, not a second copy of the rule: false when the last
     attempt left the transfer's outcome unknown, because the recorded
     replacement may already hold the member's LAMA ownership and redirecting
     is what would split it between two people. */
  const replacementLocked =
    resumed !== null &&
    resumed.recordedReplacementUserId !== null &&
    !resumed.replacementChangeAllowed;
  const canConfirm =
    active !== null &&
    !active.pending &&
    !attemptInProgress &&
    recoveryReady &&
    inventoryReady &&
    /* The one refusal a retry cannot clear: the deployment has no ownership
       client, so the next attempt fails identically. */
    active.errorCode !== OWNERSHIP_UNCONFIGURED &&
    (!requiresReplacement || replacementUserId !== "");

  const patch = (changes: Partial<Omit<Attempt, "userId">>): void =>
    setAttempt((current) =>
      current && member && current.userId === member.userId
        ? { ...current, ...changes }
        : current,
    );

  const confirm = async (): Promise<void> => {
    if (!member || !active || !canConfirm) return;
    const origin = {
      userId: member.userId,
      idempotencyKey: active.idempotencyKey,
    };
    patch({ pending: true, error: null, errorCode: null });
    try {
      await onConfirm({
        replacementUserId: replacementUserId === "" ? null : replacementUserId,
        idempotencyKey: origin.idempotencyKey,
      });
      /* Done: nothing is in flight for this member any more. Drop the cached
         recovery answer so the next open asks afresh rather than offering to
         resume a completed operation. The caller closes on success. */
      await queryClient.invalidateQueries({
        queryKey: adminLeadRemovalQueryKey(tenantId, member.userId),
      });
    } catch (error) {
      const code = error instanceof AdminApiError ? error.code : undefined;
      /* A refusal that names an in-flight operation is a RESUME instruction:
         adopt its key, stage and recorded replacement, so the next Try again
         continues that operation instead of minting another key. */
      const namedKey = adminErrorDetail(error, "idempotencyKey");
      const namedStage = adminErrorDetail(error, "stage");
      const namedReplacement = adminErrorDetail(error, "replacementUserId");
      setAttempt((current) => {
        if (
          !current ||
          current.userId !== origin.userId ||
          current.idempotencyKey !== origin.idempotencyKey
        ) {
          return current;
        }
        const next: Attempt = {
          ...current,
          pending: false,
          error: describeRemovalError(error, member.label),
          errorCode: code ?? null,
          serverRequiresReplacement:
            current.serverRequiresReplacement ||
            code === "replacement_required",
          /* An ineligible pick is cleared so the field is empty again rather
             than showing a name that will fail twice. */
          replacementUserId:
            code === "replacement_ineligible" ? "" : current.replacementUserId,
          /* `replacement_ineligible` is thrown INSIDE the transfer transaction,
             so it proves nothing moved — this is the one refusal that unlocks
             the picker, and the server agrees on the next read. */
          resumed:
            code === "replacement_ineligible" && current.resumed
              ? { ...current.resumed, replacementChangeAllowed: true }
              : current.resumed,
        };
        if (
          code === "removal_in_progress" &&
          namedKey !== null &&
          namedKey !== current.idempotencyKey
        ) {
          return {
            ...next,
            idempotencyKey: namedKey,
            resumed: {
              stage: isStage(namedStage) ? namedStage : "pending",
              lastErrorCode: adminErrorDetail(error, "lastErrorCode"),
              requestedByUserId: null,
              recordedReplacementUserId: namedReplacement,
              /* Unknown for an operation this dialog has not read: assume the
                 restrictive answer and let the recovery refetch below relax it,
                 so a redirect is never offered on a guess. */
              attemptInProgress: false,
              replacementChangeAllowed: false,
            },
            replacementUserId: namedReplacement ?? current.replacementUserId,
          };
        }
        if (code === "replacement_change_unsafe" && current.resumed) {
          /* The redirect was refused. Snap the pick back to the replacement the
             server holds and lock it, so the Try again this dialog offers is
             the one the server will accept. */
          return {
            ...next,
            replacementUserId: namedReplacement ?? current.replacementUserId,
            resumed: {
              ...current.resumed,
              stage: isStage(namedStage) ? namedStage : current.resumed.stage,
              lastErrorCode: adminErrorDetail(error, "lastErrorCode"),
              recordedReplacementUserId:
                namedReplacement ?? current.resumed.recordedReplacementUserId,
              replacementChangeAllowed: false,
            },
          };
        }
        if (code === "removal_attempt_in_progress" && current.resumed) {
          return {
            ...next,
            resumed: { ...current.resumed, attemptInProgress: true },
          };
        }
        if (code === "idempotency_key_conflict" && namedReplacement !== null) {
          /* The recorded replacement received the transfer and is still
             eligible: the only way to finish is with them. */
          return { ...next, replacementUserId: namedReplacement };
        }
        return next;
      });
      if (code === "replacement_required") void inventory.refetch();
      /* Adopted another attempt's key on a guess: ask the server what that
         operation actually allows, rather than leaving the guess on screen. */
      if (code === "removal_in_progress") void recovery.refetch();
    }
  };

  const pending = active?.pending ?? false;
  const close = (): void => {
    if (pending) return;
    onClose();
  };

  const memberLabel = member?.label ?? "This member";
  const startedByOther =
    resumed !== null &&
    resumed.requestedByUserId !== null &&
    currentUserId !== null &&
    resumed.requestedByUserId !== currentUserId;

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        resumed
          ? `Resume removing Transformation Lead from ${memberLabel}?`
          : `Remove Transformation Lead from ${memberLabel}?`
      }
      description={
        description ??
        "They keep their workspace access and their own assessment responses. Everything they own in LAMA moves to the replacement you choose."
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" disabled={pending} onClick={close}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canConfirm}
            aria-describedby="tl-removal-confirm-reason"
            onClick={() => void confirm()}
          >
            {pending
              ? resumed
                ? "Resuming…"
                : "Removing…"
              : active?.error && active.errorCode !== OWNERSHIP_UNCONFIGURED
                ? "Try again"
                : resumed
                  ? "Resume removal"
                  : "Remove Transformation Lead"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" aria-busy={pending}>
        {recovery.isPending ? (
          <p role="status" className="text-sm text-pb-text-muted">
            Checking for a removal already in progress…
          </p>
        ) : recovery.isError ? (
          <p role="alert" className="text-sm text-pb-red-text">
            Could not check whether a removal is already in progress, so this
            action is unavailable.{" "}
            {/* eslint-disable-next-line ds/no-hand-built -- kept: an accent text action inside the sentence, underlined on hover only; Button variant="link" size="inline" is cyan and underlined at rest, a look change no PR 3 row approves. */}
            <button
              type="button"
              className="rounded-sm text-pb-accent-text hover:underline focus-ring"
              onClick={() => void recovery.refetch()}
            >
              Check again
            </button>
          </p>
        ) : resumed ? (
          /* An operation exists. Say whose, how far it got, and why it stopped
             — in words, with no LAMA content — so the administrator knows what
             Resume will do. */
          <p role="status" className="text-sm text-pb-text-secondary">
            A removal for {memberLabel} is already in progress
            {startedByOther ? ", started by another admin" : ""}.{" "}
            {STAGE_COPY[resumed.stage]}
            {resumed.lastErrorCode &&
            Object.hasOwn(LAST_ERROR_COPY, resumed.lastErrorCode)
              ? ` ${LAST_ERROR_COPY[resumed.lastErrorCode]}`
              : ""}{" "}
            Resuming continues from there; nothing is repeated.
          </p>
        ) : null}

        {inventory.isPending ? (
          <p role="status" className="text-sm text-pb-text-muted">
            Checking what {memberLabel} owns in LAMA…
          </p>
        ) : inventory.isError ? (
          <p role="alert" className="text-sm text-pb-red-text">
            {isOwnershipUnconfigured(inventory.error) ? (
              /* No Check again: the deployment has no ownership client, so the
                 read cannot start rather than having failed, and a retry
                 control here only makes a configuration gap look flaky. */
              <>
                Transformation Lead removal is not configured in this
                deployment, so what {memberLabel} owns in LAMA cannot be
                checked. This needs a deployment configuration change, not a
                retry.
              </>
            ) : (
              <>
                Could not check what {memberLabel} owns in LAMA, so the removal
                is unavailable.{" "}
                {/* eslint-disable-next-line ds/no-hand-built -- kept: an accent text action inside the sentence, underlined on hover only; Button variant="link" size="inline" is cyan and underlined at rest, a look change no PR 3 row approves. */}
                <button
                  type="button"
                  className="rounded-sm text-pb-accent-text hover:underline focus-ring"
                  onClick={() => void inventory.refetch()}
                >
                  Check again
                </button>
              </>
            )}
          </p>
        ) : inventory.data ? (
          /* Look row OK-25: the label/value grid is the DescriptionList. */
          <DescriptionList
            layout="grid"
            density="compact"
            aria-label="LAMA ownership"
          >
            <DescriptionItem label={transferDone ? "Still owns" : "Owns"}>
              {countPhrase(
                inventory.data.questionnairesOwned,
                "questionnaire",
                "questionnaires",
              )}
              {", "}
              {countPhrase(
                inventory.data.campaignsOwned,
                "campaign",
                "campaigns",
              )}
            </DescriptionItem>
            <DescriptionItem label="Other LAMA access">
              {countPhrase(
                // Campaign memberships only: a questionnaire is owned or not,
                // and every published one is readable by any lead without a
                // membership, so there is nothing else to revoke.
                inventory.data.campaignMemberships,
                "membership",
                "memberships",
              )}
              {transferDone ? " — revoked" : " — will be revoked"}
            </DescriptionItem>
          </DescriptionList>
        ) : null}

        {inventoryReady && recoveryReady && !requiresReplacement ? (
          <p className="text-sm text-pb-text-secondary">
            Nothing to transfer. Their other LAMA access is revoked and they are
            signed out everywhere.
          </p>
        ) : null}

        {requiresReplacement ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-pb-text-secondary">
              {replacementLocked
                ? `The recorded replacement may already hold everything ${memberLabel} owns, so this removal has to finish with them. If they turn out to be ineligible, you will be able to choose someone else then.`
                : transferDone
                  ? `Ownership was already transferred to the recorded replacement. Keep them to finish; choose someone else only if they can no longer take over, and then only what ${memberLabel} still owns moves to the new person.`
                  : `Choose an active Transformation Lead to take over everything ${memberLabel} owns. Their other LAMA access is revoked and they are signed out everywhere.`}
            </p>
            <FormField label="Replacement Transformation Lead">
              <PeopleField
                label="Replacement Transformation Lead"
                emptyLabel="Choose a person"
                source={replacementSource}
                labels={LEAD_FIELD_LABELS}
                value={replacement}
                readOnly={replacementLocked}
                disabled={pending || leads.isPending || candidates.length === 0}
                onChange={(next) => {
                  if (next?.kind === "person") setPicked(next);
                  patch({
                    replacementUserId: next?.kind === "person" ? next.id : "",
                  });
                }}
              />
            </FormField>
            {leads.isError ? (
              <p role="alert" className="text-xs text-pb-red-text">
                Transformation Leads could not be loaded.{" "}
                {/* eslint-disable-next-line ds/no-hand-built -- kept: an accent text action inside the sentence, underlined on hover only; Button variant="link" size="inline" is cyan and underlined at rest, a look change no PR 3 row approves. */}
                <button
                  type="button"
                  className="rounded-sm text-pb-accent-text hover:underline focus-ring"
                  onClick={() => void leads.refetch()}
                >
                  Try again
                </button>
              </p>
            ) : leads.isPending ? (
              <p className="text-xs text-pb-text-muted">
                Loading Transformation Leads…
              </p>
            ) : candidates.length === 0 ? (
              <p className="text-xs text-pb-text-muted">
                No other active Transformation Lead. Grant Transformation Lead
                to someone else first, then come back to remove it from{" "}
                {memberLabel}.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* The one sentence every disabled Confirm can be explained by, kept
            in the DOM so `aria-describedby` on the button reaches it. */}
        <p id="tl-removal-confirm-reason" className="sr-only">
          {!recoveryReady
            ? "Waiting to learn whether a removal is already in progress."
            : !inventoryReady
              ? "Waiting to learn what this member owns in LAMA."
              : attemptInProgress
                ? "An attempt on this removal is already running. Wait for it to finish."
                : requiresReplacement && replacementUserId === ""
                  ? "Choose a replacement Transformation Lead first."
                  : resumed
                    ? "Resumes the recorded removal under its own key."
                    : "Removes the Transformation Lead role after transferring ownership."}
        </p>

        {/* Visible beside the disabled button, not only to a screen reader:
            a waiting admin otherwise reads a dead control as a broken one. */}
        {attemptInProgress ? (
          <p role="status" className="text-sm text-pb-text-secondary">
            An attempt on this removal is already running — possibly on another
            admin&rsquo;s screen. This dialog can resume it once that attempt
            finishes.{" "}
            {/* eslint-disable-next-line ds/no-hand-built -- kept: an accent text action inside the sentence, underlined on hover only; Button variant="link" size="inline" is cyan and underlined at rest, a look change no PR 3 row approves. */}
            <button
              type="button"
              className="rounded-sm text-pb-accent-text hover:underline focus-ring"
              onClick={() => void recovery.refetch()}
            >
              Check again
            </button>
          </p>
        ) : null}

        {active?.error ? (
          <p role="alert" className="text-sm text-pb-red-text">
            {active.error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
